import { createConnect } from './connect';
import { Event } from './event';
import { env } from './env';
import { assert } from './utils';

interface Config {
    // 应用ID， 在控制台应用详情查看
    appId: string;

    container: string;
    /**
     *  获取使用权益凭证
     */
    getUseRightCert(info: {
        appId: string;
        useKey: string;
        abilityCode: string;
        workId: string;
    }): Promise<string | false | Error>;
    /**
     * 获取用户授权标识
     */
    authorize(): Promise<string>;
}

// interface Canva {
//     // addImage(url: string, options?: { width: number; height: number }): Promise<void>;
// }

export function createMobileTemplateEditor(config: Config) {
    assert(!!config, 'createTemplateEditor 需要传入参数才能使用');
    assert(typeof config.authorize === 'function', 'authorize 必现是个 function');
    assert(typeof config.container === 'string', '缺少必填参数 container');
    assert(typeof config.getUseRightCert === 'function', '缺少必填参数 getUseRightCert');

    const event = new Event();

    let load = false;
    const tempCallbacks: Function[] = [];
    function cleanCallBack() {
        tempCallbacks.forEach((fn) => fn());
        tempCallbacks.length = 0;
    }

    const iframeManager = createConnect<{
        init(data: any): void;
        addImage(url: string, options?: { width: number; height: number }): Promise<void>;
    }>({
        appId: config.appId,
        container: config.container,
        css: 'min-height: 100vh;',
        methods: {
            async emit(name: string, ...args: any) {
                event.emit(name, ...args);
            },
            async onload() {
                event.emit('load');
            },
            async onSelectTemplate(info: any) {
                event.emit('select.template', info);
                return false;
            },
            async onTemplateLoad() {
                event.emit('template.load');
            },
            async getUseRightCert(info: any) {
                if (!config.getUseRightCert) return false;
                const token = await config.getUseRightCert(info);
                if (token instanceof Error) {
                    return {
                        message: token.message,
                    };
                }
                return token;
            },
            async getUserCode() {
                if (!config.authorize) return false;
                try {
                    return await config.authorize();
                } catch (error) {
                    close();
                }
            },
        },
    });

    async function createDesign(options: { templateId: string }) {
        cleanCallBack();
        load = false;
        const code = await config.authorize();
        const params: Record<string, any> = {
            version: 'TE002',
            user_code: code,
        };
        if (options.templateId) {
            params.id = options.templateId;
        }

        iframeManager.update(env.MOBILE_TEMPLATE_EDITOR_URL, params);

        event.once('load', async () => {
            load = true;
            const e = await iframeManager.getService();
            await e.init({
                appId: config.appId,
                version: 'TE002',
            });
            event.emit('editor.load');
        });

        iframeManager.insert();
        return new Promise<{ files: Blob[]; workId: number; title: string; type: string } | false>(
            (resolve) => {
                tempCallbacks.push(event.once('save', (v) => resolve(v)));
                tempCallbacks.push(event.once('close', () => resolve(false)));
            },
        );
    }

    async function editDesign(options: { id: string }) {
        cleanCallBack();
        load = false;
        const code = await config.authorize();

        iframeManager.update(env.MOBILE_TEMPLATE_EDITOR_URL, {
            id: options.id,
            mode: 'user',
            user_code: code,
            version: 'TE002',
        });

        event.once('load', async () => {
            load = true;
            const e = await iframeManager.getService();

            await e.init({
                appId: config.appId,
                version: 'TE002',
                v: env.VERSION,
            });
            event.emit('editor.load');
        });

        iframeManager.insert();
        return new Promise<{ files: Blob[]; workId: number; title: string; type: string } | false>(
            (resolve) => {
                tempCallbacks.push(event.once('save', (v) => resolve(v)));
                tempCallbacks.push(event.once('close', () => resolve(false)));
            },
        );
    }

    function openTemplateList(options: { onSelectTemplate?(info: { id: string }): void }) {
        load = false;
        cleanCallBack();
        iframeManager.update(env.OPEN_PLATFORM_ORIGIN + '/mt/templates', {
            app_id: config.appId,
            version: 'TE002',
        });
        event.once('load', async () => {
            load = true;
            const e = await iframeManager.getService();
            e.init({
                appId: config.appId,
                v: env.VERSION,
                version: 'TE002',
            });
        });
        if (options.onSelectTemplate) {
            tempCallbacks.push(event.add('select.template', options.onSelectTemplate));
        }
        iframeManager.insert();
        return new Promise<{ id: number } | false>((resolve) => {
            tempCallbacks.push(event.once('select.template', (v) => resolve(v)));
            tempCallbacks.push(event.once('close', () => resolve(false)));
        });
    }

    function close() {
        event.emit('close');
        iframeManager.remove();
    }

    function onSave(
        fn: (info: { workId: string; files: Blob[]; title: string; type: string }) => void,
    ) {
        event.add('save', fn);
    }

    function onClose(fn: () => any) {
        event.add('close', fn);
    }

    function onLoad(fn: () => any) {
        event.add('editor.load', fn);
    }

    function onTemplateLoad(fn: () => any) {
        event.add('template.load', fn);
    }

    return {
        get isLoad() {
            return load;
        },
        openTemplateList,
        close,
        onSave,
        onTemplateLoad,
        onClose,
        onLoad,
        editDesign,
        createDesign,
    };
}
